/**
 * Custom hooks for email accounts management
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/lib/api-client";
import { fetchEmailCounts } from "@/data/api/email";
import type {
    EmailAccountResponse,
    EmailAccountCreate,
    EmailAccountCreateIMAP,
    EmailAccountUpdate,
    EmailAccountUpdateIMAP,
    EmailAccountFilters,
    OAuthAuthorizationResponse,
    ProviderPreset,
    ConnectionTestResponse,
    ProcessEmailsResponse,
    EmailMessageResponse,
    EmailMessageDetailResponse,
    EmailMessageFilters,
    EmailFolderInfo,
} from "@/types/api/email";

// ============================================================================
// Query Keys
// ============================================================================

export const EMAIL_ACCOUNT_KEYS = {
    all: ["email-accounts"] as const,
    lists: () => [...EMAIL_ACCOUNT_KEYS.all, "list"] as const,
    list: (filters?: EmailAccountFilters) => [...EMAIL_ACCOUNT_KEYS.lists(), filters] as const,
    details: () => [...EMAIL_ACCOUNT_KEYS.all, "detail"] as const,
    detail: (id: string) => [...EMAIL_ACCOUNT_KEYS.details(), id] as const,
    folders: (id: string) => [...EMAIL_ACCOUNT_KEYS.all, "folders", id] as const,
    presets: ["email-imap-presets"] as const,
    messages: (filters?: EmailMessageFilters) => ["email-messages", filters] as const,
    message: (id: string) => ["email-message", id] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get list of email accounts
 *
 * Note: staleTime is set to 0 to ensure last_sync_at updates immediately after sync.
 * The global default is 5 minutes which would prevent UI updates.
 */
export function useEmailAccounts(filters?: EmailAccountFilters) {
    return useQuery({
        queryKey: EMAIL_ACCOUNT_KEYS.list(filters),
        queryFn: async (): Promise<EmailAccountResponse[]> => {
            const params: Record<string, string | number> = {};
            if (filters?.skip !== undefined) params.skip = filters.skip;
            if (filters?.limit !== undefined) params.limit = filters.limit;
            if (filters?.status) params.status = filters.status;

            return await apiGet<EmailAccountResponse[]>("/email/accounts", { params });
        },
        staleTime: 0, // Always refetch to get updated last_sync_at
    });
}

/**
 * Hook to get a single email account
 */
export function useEmailAccount(accountId: string) {
    return useQuery({
        queryKey: EMAIL_ACCOUNT_KEYS.detail(accountId),
        queryFn: async (): Promise<EmailAccountResponse> => {
            return await apiGet<EmailAccountResponse>(`/email/accounts/${accountId}`);
        },
        enabled: !!accountId,
    });
}

/**
 * Hook to get available folders/labels for an email account
 */
export function useEmailAccountFolders(accountId: string, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: EMAIL_ACCOUNT_KEYS.folders(accountId),
        queryFn: async (): Promise<EmailFolderInfo[]> => {
            return await apiGet<EmailFolderInfo[]>(`/email/accounts/${accountId}/folders`);
        },
        enabled: options?.enabled !== false && !!accountId,
        staleTime: 5 * 60 * 1000, // 5 minutes - folders don't change often
    });
}

/**
 * Hook to get IMAP provider presets
 */
export function useImapProviderPresets() {
    return useQuery({
        queryKey: EMAIL_ACCOUNT_KEYS.presets,
        queryFn: async (): Promise<ProviderPreset[]> => {
            return await apiGet<ProviderPreset[]>("/email/providers/imap/presets");
        },
        staleTime: 24 * 60 * 60 * 1000, // 24 hours - presets rarely change
    });
}

/**
 * Hook to get email messages
 */
export function useEmailMessages(filters?: EmailMessageFilters) {
    return useQuery({
        queryKey: EMAIL_ACCOUNT_KEYS.messages(filters),
        queryFn: async (): Promise<EmailMessageResponse[]> => {
            const params: Record<string, string | number> = {};
            if (filters?.skip !== undefined) params.skip = filters.skip;
            if (filters?.limit !== undefined) params.limit = filters.limit;
            if (filters?.status) params.status = filters.status;
            if (filters?.account_id) params.account_id = filters.account_id;

            return await apiGet<EmailMessageResponse[]>("/email/messages", { params });
        },
    });
}

/**
 * Hook to get a single email message with full details and attachments
 */
export function useEmailMessage(messageId: string, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: EMAIL_ACCOUNT_KEYS.message(messageId),
        queryFn: async (): Promise<EmailMessageDetailResponse> => {
            return await apiGet<EmailMessageDetailResponse>(`/email/messages/${messageId}`);
        },
        enabled: options?.enabled !== false && !!messageId,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create an OAuth email account (Gmail/Outlook)
 */
export function useCreateEmailAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: EmailAccountCreate): Promise<EmailAccountResponse> => {
            return await apiPost<EmailAccountResponse>("/email/accounts", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

/**
 * Hook to create an IMAP/SMTP email account
 */
export function useCreateImapEmailAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: EmailAccountCreateIMAP): Promise<EmailAccountResponse> => {
            return await apiPost<EmailAccountResponse>("/email/accounts/imap", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

/**
 * Hook to update an email account
 */
export function useUpdateEmailAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            accountId,
            data,
        }: {
            accountId: string;
            data: EmailAccountUpdate;
        }): Promise<EmailAccountResponse> => {
            return await apiPut<EmailAccountResponse>(`/email/accounts/${accountId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.detail(variables.accountId) });
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

/**
 * Hook to update an IMAP/SMTP email account
 */
export function useUpdateImapEmailAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            accountId,
            data,
        }: {
            accountId: string;
            data: EmailAccountUpdateIMAP;
        }): Promise<EmailAccountResponse> => {
            return await apiPut<EmailAccountResponse>(`/email/accounts/imap/${accountId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.detail(variables.accountId) });
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

/**
 * Hook to delete an email account
 */
export function useDeleteEmailAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountId: string): Promise<void> => {
            return await apiDelete<void>(`/email/accounts/${accountId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

/**
 * Hook to get Gmail OAuth authorization URL
 */
export function useGetGmailAuthUrl() {
    return useMutation({
        mutationFn: async (accountId: string): Promise<OAuthAuthorizationResponse> => {
            return await apiGet<OAuthAuthorizationResponse>(
                `/email/accounts/${accountId}/oauth/authorize`
            );
        },
    });
}

/**
 * Hook to get Outlook OAuth authorization URL
 */
export function useGetOutlookAuthUrl() {
    return useMutation({
        mutationFn: async (accountId: string): Promise<OAuthAuthorizationResponse> => {
            return await apiGet<OAuthAuthorizationResponse>(
                `/email/accounts/${accountId}/oauth/authorize/outlook`
            );
        },
    });
}

/**
 * Hook to test email connection (IMAP/SMTP)
 */
export function useTestEmailConnection() {
    return useMutation({
        mutationFn: async (accountId: string): Promise<ConnectionTestResponse> => {
            return await apiPost<ConnectionTestResponse>(
                `/email/accounts/${accountId}/test-connection`
            );
        },
    });
}

/**
 * Hook to process emails for an account
 *
 * Invalidates the accounts list to update last_sync_at display.
 * The component also calls onRefresh() manually, but invalidateQueries
 * deduplicates requests so there won't be duplicate API calls.
 */
export function useProcessAccountEmails() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountId: string): Promise<ProcessEmailsResponse> => {
            return await apiPost<ProcessEmailsResponse>(
                `/email/accounts/${accountId}/process`
            );
        },
        onSuccess: (_data, accountId) => {
            // Refresh specific account detail
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.detail(accountId) });
            // Refresh messages list (new emails may have been fetched)
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.messages() });
            // Refresh accounts list to update last_sync_at in the UI
            // Using refetchType: 'all' to force immediate refetch
            queryClient.invalidateQueries({
                queryKey: EMAIL_ACCOUNT_KEYS.lists(),
                refetchType: 'all'
            });
            // Refresh email counts (new emails affect counts)
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

/**
 * Hook to refresh Gmail OAuth token
 */
export function useRefreshGmailToken() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountId: string): Promise<{ status: string; message: string }> => {
            return await apiPost<{ status: string; message: string }>(
                `/email/accounts/${accountId}/oauth/refresh`
            );
        },
        onSuccess: (_data, accountId) => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.detail(accountId) });
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

/**
 * Hook to refresh Outlook OAuth token
 */
export function useRefreshOutlookToken() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountId: string): Promise<{ status: string; message: string }> => {
            return await apiPost<{ status: string; message: string }>(
                `/email/accounts/${accountId}/oauth/refresh/outlook`
            );
        },
        onSuccess: (_data, accountId) => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.detail(accountId) });
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

/**
 * Hook to toggle email account sync status (enable/disable)
 */
export function useToggleEmailAccountSync() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            accountId,
            enabled,
        }: {
            accountId: string;
            enabled: boolean;
        }): Promise<EmailAccountResponse> => {
            return await apiPatch<EmailAccountResponse>(
                `/email/accounts/${accountId}/toggle-sync`,
                { enabled }
            );
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.detail(variables.accountId) });
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
        },
    });
}

// ============================================================================
// Provider Detection
// ============================================================================

export interface EmailProviderDetectionResult {
    email: string;
    domain: string;
    detected_provider: "gmail" | "outlook" | null;
    provider_name: string;
    confidence: "high" | "low";
    mx_records: string[];
}

/**
 * Hook to detect email provider via DNS MX records lookup.
 * Detects Google Workspace and Microsoft 365 on custom domains.
 *
 * @param email - Email address to check
 * @param enabled - Whether to run the query
 */
export function useDetectEmailProvider(email: string, enabled: boolean = true) {
    return useQuery({
        queryKey: ["email-provider-detection", email],
        queryFn: async (): Promise<EmailProviderDetectionResult> => {
            return await apiGet<EmailProviderDetectionResult>("/email/detect-provider", {
                params: { email },
            });
        },
        enabled: enabled && !!email && email.includes("@") && email.split("@")[1]?.length > 2,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes per domain
        retry: false,
    });
}

// ============================================================================
// New Hooks for Trade Comms Features
// ============================================================================

/**
 * Hook to process all email accounts
 * Iterates through all accounts and calls process for each
 */
export function useProcessAllEmailAccounts() {
    const queryClient = useQueryClient();
    const { data: accounts } = useEmailAccounts({});

    return useMutation({
        mutationFn: async () => {
            if (!accounts || accounts.length === 0) {
                throw new Error('No email accounts found');
            }

            // Process all accounts in parallel
            const promises = accounts.map(account =>
                apiPost(`/email/accounts/${account.account_id}/process`)
            );

            return await Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: ['activities'] });
            queryClient.invalidateQueries({ queryKey: ['email-limbo'] });
            // Refresh email counts (sync affects counts)
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        }
    });
}

/**
 * Hook to manually associate an email with a container
 */
export function useAssociateEmailToContainer() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            messageId,
            containerId,
        }: {
            messageId: string;
            containerId: string;
        }) =>
            apiPatch(`/email/messages/${messageId}/associate-container`, {
                container_id: containerId,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['activities'] });
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.messages() });
            queryClient.invalidateQueries({ queryKey: ['containers'] });
            // Refresh email counts (association might affect counts if it processes the email)
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

/** Response from GET attachment download endpoint */
export interface EmailAttachmentDownloadResponse {
    download_url: string;
    expires_at: string;
    file_name: string;
    file_size_bytes: number | null;
    content_type: string | null;
}

/**
 * Fetch a signed download URL for an email attachment.
 * Use for preview (images/PDF) or download link.
 */
export async function getEmailAttachmentDownloadUrl(
    messageId: string,
    attachmentId: string,
    expirationHours: number = 1
): Promise<EmailAttachmentDownloadResponse> {
    return apiGet<EmailAttachmentDownloadResponse>(
        `/email/messages/${messageId}/attachments/${attachmentId}/download`,
        { params: { expiration_hours: expirationHours } }
    );
}

/**
 * Hook to set whether an email attachment is excluded from processing (user marked as not relevant).
 */
export function useSetEmailAttachmentExcluded() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            messageId,
            attachmentId,
            excluded,
        }: {
            messageId: string;
            attachmentId: string;
            excluded: boolean;
        }) =>
            apiPatch(`/email/messages/${messageId}/attachments/${attachmentId}`, {
                excluded,
            }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.message(variables.messageId) });
        },
    });
}

/**
 * Hook to mark an email as not in context (reject)
 */
export function useMarkEmailAsNotInContext() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (messageId: string) =>
            apiPost(`/email/messages/${messageId}/review`, {
                action: 'reject',
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['activities'] });
            queryClient.invalidateQueries({ queryKey: EMAIL_ACCOUNT_KEYS.messages() });
            // Refresh email counts (rejection moves email out of limbo)
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

/**
 * Hook to fetch total email counts across all accounts
 * Returns real database counts, not limited by pagination
 */
export function useEmailCounts(options?: {
    enabled?: boolean;
    refetchInterval?: number;
}) {
    return useQuery({
        queryKey: ['email', 'counts'],
        queryFn: () => fetchEmailCounts(),
        enabled: options?.enabled ?? true,
        refetchInterval: options?.refetchInterval ?? 60000, // Polling ogni 60s
    });
}
